import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { ContractExpirationRow } from "@/contractExpirations/contractExpiration";
import {
  type ContractExpirationColumn,
  getContractExpirationColumns,
} from "@/contractExpirations/contractExpirationColumns";
import { getExpirationVehicleTitle } from "@/contractExpirations/contractExpirationFields";
import {
  isContractExpirationDecisionFilter,
  isContractExpirationStatusFilter,
  toExpirationStatusQuery,
} from "@/contractExpirations/contractExpirationLabels";
import { isExpirationPeriodOrdered } from "@/contractExpirations/contractExpirationPeriod";
import {
  ContractExpirationFilters,
  type ContractExpirationFilterValues,
} from "@/contractExpirations/ContractExpirationFilters";
import { useGetAdminContractExpirations } from "@/lib/api/generated/contract-expiration/contract-expiration";
import { cn } from "@rumbledirect/ui-v2/cn";
import { toPeriodDate } from "@/lib/periodDate";

/** Matches `paginationItemsPerPage` on the backend's `ContractExpirationResource`. */
const ITEMS_PER_PAGE = 20;

/** Enough skeleton rows to fill the fold without pretending to know the count. */
const SKELETON_ROW_KEYS = Array.from(
  { length: 10 },
  (_, index) => `contract-expiration-skeleton-${index}`,
);

interface ContractExpirationSearch extends ContractExpirationFilterValues {
  readonly page: number;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

export const Route = createFileRoute("/_authenticated/contract-expirations")({
  // The whole filter state lives in the URL so a view can be shared or
  // bookmarked. The status opens on "openstaand" rather than empty, because the
  // page is a work list: the default question is what still needs doing, not
  // what has ever been signalled.
  validateSearch: (
    search: Record<string, unknown>,
  ): ContractExpirationSearch => {
    const page = Number(search.page);
    const status = search.status;
    const decision = search.decision;

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      endsFrom: toPeriodDate(search.endsFrom),
      endsUntil: toPeriodDate(search.endsUntil),
      site: toOptionalString(search.site),
      leaseCompany: toOptionalString(search.leaseCompany),
      status: isContractExpirationStatusFilter(status) ? status : "open",
      decision: isContractExpirationDecisionFilter(decision)
        ? decision
        : undefined,
    };
  },
  component: ContractExpirationsPage,
});

function ContractExpirationsPage(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { page, endsFrom, endsUntil, site, leaseCompany, status, decision } =
    search;

  const periodOrdered = isExpirationPeriodOrdered(search);
  const statusQuery = toExpirationStatusQuery(status);

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminContractExpirations(
      {
        page,
        ...(endsFrom ? { endsFrom } : {}),
        ...(endsUntil ? { endsUntil } : {}),
        ...(site ? { site } : {}),
        ...(leaseCompany ? { leaseCompany } : {}),
        ...(statusQuery ? { "expirationStatus[]": [...statusQuery] } : {}),
        ...(decision ? { "expirationDecision[]": [decision] } : {}),
      },
      {
        query: {
          placeholderData: keepPreviousData,
          // An inverted period is a typo, not a question: the filter bar says
          // so and the request stays unsent.
          enabled: periodOrdered,
        },
      },
    );

  // Rebuilt per render rather than at module load, unlike the contract monitor:
  // the countdown column is relative to today, so a tab left open overnight
  // would otherwise keep counting from yesterday.
  const columns = getContractExpirationColumns();
  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;
  const columnCount = columns.length + 1;

  /** Any filter change resets to page 1; page 7 of the old result set is meaningless. */
  const applyFilters = (
    patch: Partial<ContractExpirationFilterValues>,
  ): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Contractexpiraties
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          De contracten waarvan het einde in zicht is, en hoe ver het vervolg
          geregeld is.
        </p>
      </div>

      <ContractExpirationFilters values={search} onChange={applyFilters} />

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              {columns.map((column) => (
                <Th key={column.id} className={column.visibilityClassName}>
                  {column.header}
                </Th>
              ))}
              <Th className="w-12">
                <span className="sr-only">Expiratie openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ContractExpirationRows
              columns={columns}
              columnCount={columnCount}
              isPending={isPending && periodOrdered}
              isError={isError}
              periodOrdered={periodOrdered}
              members={members}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function ContractExpirationRows({
  columns,
  columnCount,
  isPending,
  isError,
  periodOrdered,
  members,
  onRetry,
}: {
  readonly columns: readonly ContractExpirationColumn[];
  readonly columnCount: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly periodOrdered: boolean;
  readonly members: readonly ContractExpirationRow[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (!periodOrdered) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <p className="text-muted-foreground">
            Kies een periode die vooruit loopt om expiraties te zien.
          </p>
        </Td>
      </Tr>
    );
  }

  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        {columns.map((column) => (
          <Td key={column.id} className={column.visibilityClassName}>
            <Skeleton className="h-4 w-24" />
          </Td>
        ))}
        <Td>
          <Skeleton className="h-4 w-4" />
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de contractexpiraties niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <p className="text-muted-foreground">
            Geen contractexpiraties met deze filters.
          </p>
        </Td>
      </Tr>
    );
  }

  return members.map((row) => {
    const vehicle = row.licensePlate ?? getExpirationVehicleTitle(row);

    return (
      <Tr key={row.id}>
        {columns.map((column) => (
          <Td
            key={column.id}
            className={cn(column.visibilityClassName, column.cellClassName)}
          >
            {column.cell(row)}
          </Td>
        ))}
        <Td className="text-right">
          {row.id ? (
            <Link
              to="/contract-expirations/$contractExpirationId"
              params={{ contractExpirationId: row.id }}
              aria-label={`Expiratie van ${vehicle} openen`}
              className="inline-grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </Link>
          ) : null}
        </Td>
      </Tr>
    );
  });
}
